/**
 * Guarded `localStorage` / `sessionStorage` access.
 *
 * Every browser-storage call in the app goes through here (see the "Browser storage" rule in
 * CLAUDE.md). Storage is a convenience: the app must keep working when it is unavailable, and no
 * storage failure may ever propagate as an exception into application code.
 *
 * Storage throws for reasons that have nothing to do with how much was stored, and with storage
 * nearly empty: Safari private windows, "block all cookies" / third-party-blocked iframes, disk
 * pressure, evicted site data. Safari and Firefox word the refusal
 * `QuotaExceededError: The quota has been exceeded.` -- a message users have reported verbatim from
 * a snackbar. Reads throw in the same conditions writes do, so reads are guarded too.
 *
 * Callers name the AREA (`'local'` / `'session'`), never the global object. That is deliberate and
 * is why this module resolves the area itself: Chrome with "block all cookies", and any sandboxed
 * iframe without `allow-same-origin`, throw `SecurityError` from the `window.localStorage` PROPERTY
 * GETTER, before a method is ever called. A helper taking a `Storage` argument would be handed that
 * exception at the call site, outside every guard in here.
 *
 * The failure that motivated this module: the NgRx persistence metareducer wrote state here from
 * INSIDE the dispatch chain. NgRx builds state as a `scan` over the action stream, so the throw did
 * not merely lose one snapshot -- it errored the state observable and killed the store for the rest
 * of the page's life (selectors stopped emitting, later dispatches stopped reducing, UI frozen until
 * reload).
 */

/** Which browser storage area to use. Callers pass this instead of naming the global. */
export type StorageArea = 'local' | 'session';

/**
 * Which side of a storage access failed. `removeItem` / `clear` count as writes.
 *
 * `serialize` is NOT a storage failure: the browser was never asked. It is tracked as its own op so
 * a `JSON.stringify` bug in one caller (a cycle, a BigInt) cannot consume the single warning that a
 * genuinely refused write would otherwise have produced, and so the message can name the real cause.
 */
export type StorageOp = 'read' | 'write' | 'serialize';

/**
 * One warning per op per page, not per call. A single refusal is usually followed by a refusal on
 * every subsequent access -- the metareducer alone writes on every non-router action -- so logging
 * each one would bury the console. Tracked per op because a read failure at boot and a write failure
 * later have different diagnostic value, and one must not swallow the other's only warning.
 */
const reported: Record<StorageOp, boolean> = { read: false, write: false, serialize: false };

/**
 * Everything known about one refused storage access, handed to the sink below.
 *
 * `raw` is the exact string the browser refused, and `source` the value it was serialized from --
 * both are present only for writes. `raw` means the consumer never has to re-serialize the whole
 * payload to learn its size, and `source` never has to re-parse it to walk its slices: the blob
 * refused here is routinely megabytes, and this runs on the failure path of every dispatch.
 * (A consumer that breaks the payload down per slice still pays a `JSON.stringify` per slice --
 * see `buildQuotaDiag`, which is why it runs off the dispatch chain.)
 */
export interface StorageFailure {
  area: StorageArea;
  op: StorageOp;
  /** `null` for whole-area failures (`clear`, or the area getter itself refusing). */
  key: string | null;
  /** The string the browser refused, when there was one. */
  raw: string | null;
  /** The value `raw` was serialized from, when the caller passed an object. */
  source?: unknown;
  error: unknown;
}

/** Notified on EVERY storage failure -- unlike the console warning, which is latched once per op. */
export type StorageFailureSink = (failure: StorageFailure) => void;

let failureSink: StorageFailureSink | null = null;

/**
 * Re-entrancy latch. A sink typically wants to persist what it learned, and the storage it would
 * persist to is the storage that just refused -- so its own failure would call it again, forever.
 * Also held while this module writes on its own behalf (the ceiling probe), whose failures are
 * expected and are the measurement, not an incident to report.
 */
let inSink = false;

/**
 * Register (or clear, with `null`) the failure sink. One sink at a time: the point is a single
 * app-wide diagnostic consumer, not an event bus. Registering does not replay past failures -- the
 * consumer is created at boot, and anything that fails before that is genuinely unobserved here.
 */
export function setStorageFailureSink(sink: StorageFailureSink | null): void {
  failureSink = sink;
}

/**
 * Unregister a sink, but only if it is still the registered one.
 *
 * A consumer tearing down (a destroyed injector, a spec's TestBed) must not silently disable a
 * NEWER consumer that registered after it -- with a bare `setStorageFailureSink(null)` the loser of
 * that race leaves the app with no probe at all, and nothing says so.
 */
export function clearStorageFailureSink(sink: StorageFailureSink): void {
  if (failureSink === sink) {
    failureSink = null;
  }
}

/**
 * Run `fn` with the failure sink held off, restoring the previous state afterwards.
 *
 * A sink typically persists what it learned into the storage that just refused, so its own failures
 * would call it again -- the latch below covers that while the sink runs INLINE. A sink that defers
 * its work (`QuotaDiagReporterService` does, to get off the NgRx dispatch chain) leaves that window
 * and has to re-enter it explicitly, or its own refused write re-enters the sink on the next task.
 */
export function runWithoutFailureSink<T>(fn: () => T): T {
  const wasInSink = inSink;
  inSink = true;
  try {
    return fn();
  } finally {
    inSink = wasInSink;
  }
}

/** Hand a failure to the sink, never letting the sink's own trouble reach the calling app code. */
function notifyFailureSink(failure: StorageFailure): void {
  if (failureSink === null || inSink) {
    return;
  }
  inSink = true;
  try {
    failureSink(failure);
  } catch {
    // A diagnostic that breaks the thing it observes is worse than no diagnostic.
  } finally {
    inSink = false;
  }
}

/**
 * Warn once per op per page. Deliberately does NOT claim persistence has been switched off: the
 * caller keeps trying on every later access (a refusal can be transient -- a full disk that frees
 * up, a quota that is re-granted), it just fails silently from here on.
 */
function reportStorageFailure(
  area: StorageArea,
  op: StorageOp,
  key: string | null,
  error: unknown,
  raw: string | null = null,
  source?: unknown,
): void {
  // The sink is notified BEFORE the warn-once latch, and on every failure: the latch exists to keep
  // the console readable, whereas a diagnostic consumer wants the LATEST failure (the payload grows
  // between dispatches, so the first refusal is the least informative one it could report).
  notifyFailureSink({ area, op, key, raw, source, error });
  if (reported[op]) {
    return;
  }
  reported[op] = true;
  const where = key === null ? '' : ` (key "${key}")`;
  const what = op === 'serialize'
    ? `[safe-storage] a value could not be serialized for storage${where}. Nothing was written; ` +
      'this is a bug in the value (a cycle, a BigInt) rather than a browser refusal'
    : `[safe-storage] browser storage refused a ${op}${where}. The value was not ` +
      `${op === 'read' ? 'read' : 'saved'}; the app continues without it`;
  console.warn(`${what} and later failures of this kind are not logged again.`, error);
}

/**
 * Clears the once-per-page warning latch so spec order cannot decide what gets logged.
 *
 * @internal test hook -- not part of the storage contract; no application code should call it.
 */
export function resetStorageFailureLog(): void {
  reported.read = false;
  reported.write = false;
  reported.serialize = false;
  inSink = false;
}

/**
 * Resolve the storage object, or `null` when the browser refuses to hand it over.
 *
 * The property access sits inside the try on purpose: in a sandboxed iframe or with third-party
 * storage blocked, merely reading `window.localStorage` throws `SecurityError`, so an unguarded
 * mention of the global is enough to take down whatever ran it -- app boot, in the case of
 * `purgeLegacyAuthStorage` and the locale resolvers.
 */
function resolveArea(area: StorageArea, op: StorageOp, key: string | null): Storage | null {
  try {
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch (error) {
    reportStorageFailure(area, op, key, error);
    return null;
  }
}

/** Read a raw string, or `null` when absent OR when storage refuses the read. */
export function readStorage(area: StorageArea, key: string): string | null {
  const storage = resolveArea(area, 'read', key);
  if (storage === null) {
    return null;
  }
  try {
    return storage.getItem(key);
  } catch (error) {
    reportStorageFailure(area, 'read', key, error);
    return null;
  }
}

/**
 * Read and parse a JSON value, falling back on a refused read, an absent key, or a corrupt payload.
 * A corrupt payload is NOT reported: it is an ordinary stale-format case, not a storage failure.
 */
export function readStorageJson<T>(area: StorageArea, key: string, fallback: T): T {
  const raw = readStorage(area, key);
  if (raw === null || raw === '') {
    return fallback;
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? fallback : (parsed as T);
  } catch {
    return fallback;
  }
}

/**
 * Write a raw string. Returns whether it was stored, for the rare caller that must know.
 *
 * @param source @internal diagnostic plumbing -- the OBJECT `value` was serialized from, passed by
 *        `writeStorageJson` so a failure sink can walk the payload's slices without re-parsing a
 *        multi-megabyte string. Application code must NOT pass it: it reaches the failure sink,
 *        which reports sizes and counts to the backend, and the "no user data leaves the browser"
 *        guarantee of that report rests on nothing but this. Call `writeStorageJson` instead --
 *        it fills this in for you and is the only caller that should.
 */
export function writeStorage(area: StorageArea, key: string, value: string, source?: unknown): boolean {
  const storage = resolveArea(area, 'write', key);
  if (storage === null) {
    return false;
  }
  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    reportStorageFailure(area, 'write', key, error, value, source);
    return false;
  }
}

/**
 * Serialize and write. `JSON.stringify` itself can throw (cycles, BigInt), so it is inside the guard
 * -- but it is reported as a `serialize` failure, not a `write` one: the browser never refused
 * anything, and letting a caller's cyclic value burn the write warning would hide the next real
 * refusal for the rest of the page.
 *
 * A value that stringifies to `undefined` (`undefined` itself, a function, a symbol) is the same
 * class of caller bug and is treated the same way: writing it would store the literal text
 * "undefined", which then fails to parse on the way back -- a poisoned key, reported to the caller
 * as a success.
 */
export function writeStorageJson(area: StorageArea, key: string, value: unknown): boolean {
  let raw: string | undefined;
  try {
    raw = JSON.stringify(value);
  } catch (error) {
    reportStorageFailure(area, 'serialize', key, error);
    return false;
  }
  if (raw === undefined) {
    reportStorageFailure(
      area, 'serialize', key,
      new TypeError(`a value of type ${typeof value} has no JSON representation`),
    );
    return false;
  }
  // The already-serialized string AND the value it came from are both handed down, so a failure
  // sink can size the payload and walk its slices without stringifying anything a second time.
  return writeStorage(area, key, raw, value);
}

/** Remove a key, ignoring a refusal. */
export function removeStorage(area: StorageArea, key: string): void {
  const storage = resolveArea(area, 'write', key);
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch (error) {
    reportStorageFailure(area, 'write', key, error);
  }
}

/** Wipe a whole storage area, ignoring a refusal. */
export function clearStorage(area: StorageArea): void {
  const storage = resolveArea(area, 'write', null);
  if (storage === null) {
    return;
  }
  try {
    storage.clear();
  } catch (error) {
    reportStorageFailure(area, 'write', null, error);
  }
}

/**
 * Every key currently in the area, and nothing else.
 *
 * The key-only sibling of {@link inventoryStorage}, for a caller that must ANSWER a question about
 * the area rather than describe it: did a `clear` actually empty it? `clearStorage` swallows a
 * refusal by design, so re-reading the keys is the only way to tell a wipe that happened from one
 * the browser declined -- and the caller that asks (the kiosk site-data wipe) is making a claim to
 * an operator about a shared device, where "cleared" and "refused" must not look the same.
 *
 * It takes no VALUES, unlike `inventoryStorage`, which materializes every one of them to measure it:
 * the area this is asked about is by definition one a clear was just attempted on, so it either
 * holds nothing or holds everything the clear failed to remove -- and reading a megabyte back to
 * count it is the cost that function documents paying deliberately for a diagnostic and this one has
 * no reason to pay.
 *
 * An area that cannot be reached at all answers `[]`, the same as an empty one. That is not a
 * conflation this can resolve: an inaccessible area holds nothing this app can see or has ever
 * written, so a caller reading it as empty is reading it correctly.
 */
export function storageKeys(area: StorageArea): string[] {
  const storage = resolveArea(area, 'read', null);
  if (storage === null) {
    return [];
  }
  const keys: string[] = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key !== null) {
        keys.push(key);
      }
    }
  } catch {
    // Enumeration itself can be refused; keep what we have, as `inventoryStorage` does.
  }
  return keys;
}

/**
 * Every key currently in the area with the length of its stored string, biggest first.
 *
 * For diagnostics only: it answers "what is actually taking up room" without ever rendering a
 * stored VALUE, which is why it returns lengths rather than contents -- persisted state routinely
 * holds customer data, and a diagnostic that ships user data is a leak, not a measurement.
 * A refusal mid-enumeration yields whatever was collected so far; partial evidence beats none.
 *
 * <p>ACCEPTED COST: measuring means materializing. There is no size API on `Storage` -- `getItem`
 * is the only way to learn a value's length -- so this briefly holds a copy of the whole area in
 * JS strings, which scales with exactly the problem it is diagnosing (a multi-megabyte blob) on a
 * machine that may already be under memory or disk pressure. `navigator.storage.estimate()` is the
 * only cheaper answer and it is async and origin-wide, i.e. it cannot attribute anything to a key.
 * The cost is paid deliberately: the caller runs this OFF the dispatch chain (see
 * `QuotaDiagReporterService`), one entry per key is dropped as soon as its length is taken, and the
 * per-page report budget caps how often it can happen at all.
 */
export function inventoryStorage(area: StorageArea): Array<[string, number]> {
  const storage = resolveArea(area, 'read', null);
  if (storage === null) {
    return [];
  }
  const entries: Array<[string, number]> = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key === null) {
        continue;
      }
      entries.push([key, (storage.getItem(key) ?? '').length]);
    }
  } catch {
    // Enumeration itself can be refused; keep what we have.
  }
  return entries.sort((a, b) => b[1] - a[1]);
}

/** Sizes (in characters) the ceiling probe tries, largest first. */
const CEILING_PROBE_SIZES = [4194304, 2097152, 1048576, 524288, 262144, 65536, 4096];

/**
 * Highest rung tried when the failure carried no payload to scale to (a refused read, `clear`, or
 * the area getter).
 *
 * Such a failure asks only "does this area accept anything at all?" -- the answer that separates
 * "storage is refusing outright" from "storage works, that one value was too big". Walking down
 * from 4MB to answer it would allocate megabytes and hand them to an area already under pressure,
 * for a number nothing reads. 64KB is comfortably above any single non-blob value the app stores.
 */
const NO_PAYLOAD_PROBE_CEILING = 65536;

/**
 * The rungs worth trying for a payload of `upTo` characters: the ladder with every rung larger than
 * the smallest one that would have held the payload dropped.
 *
 * Probing above the refused size answers a question nobody asked, and answers it expensively -- it
 * allocates a multi-megabyte string and asks an area already under pressure to store it, on the
 * failure path. A 40KB write that was refused needs to know whether 64KB / 4KB fit, not whether 4MB
 * does. Without a hint the ladder starts at `NO_PAYLOAD_PROBE_CEILING` for the same reason.
 */
function ceilingProbeSizes(upTo?: number | null): number[] {
  if (upTo === undefined || upTo === null || !Number.isFinite(upTo)) {
    return CEILING_PROBE_SIZES.filter(size => size <= NO_PAYLOAD_PROBE_CEILING);
  }
  let start = 0;
  for (let i = 0; i < CEILING_PROBE_SIZES.length; i++) {
    if (CEILING_PROBE_SIZES[i] < upTo) {
      break;
    }
    start = i;
  }
  return CEILING_PROBE_SIZES.slice(start);
}

/** Key the ceiling probe writes to. Removed again whether or not the write succeeded. */
const CEILING_PROBE_KEY = '__safe_storage_probe';

/**
 * Drop the probe key if a previous run left it behind.
 *
 * A probe rung that SUCCEEDS stores up to `NO_PAYLOAD_PROBE_CEILING` (or the payload size) worth of
 * filler, and its cleanup `removeItem` can itself be refused -- which would leave the diagnostic
 * permanently occupying the very quota it exists to measure, and nothing else ever writes that key.
 * So removal is attempted twice: first directly, then after shrinking the value to one character,
 * which is the smallest footprint reachable when removal keeps failing.
 *
 * Called at the top of every probe and once at boot by the diagnostic consumer, so a leftover from
 * a previous page cannot survive.
 */
export function purgeCeilingProbeKey(area: StorageArea): void {
  let storage: Storage | null;
  try {
    storage = area === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return; // Area unreachable: nothing was ever written through it either.
  }
  try {
    storage.removeItem(CEILING_PROBE_KEY);
    return;
  } catch {
    // Fall through to the shrink attempt below.
  }
  try {
    storage.setItem(CEILING_PROBE_KEY, 'x');
    storage.removeItem(CEILING_PROBE_KEY);
  } catch {
    // Nothing further to try; the key is at most one character bigger than it has to be.
  }
}

/**
 * Largest single value the area will accept RIGHT NOW, in characters, or `null` when it refuses
 * even the smallest probe (storage is unavailable rather than full).
 *
 * This is the measurement that tells the two failure modes apart, and neither is guessable from a
 * dump of what is stored: a payload that outgrew a healthy quota (probe succeeds at a size well
 * above the stored total, so the app is asking for too much at once) versus storage refusing
 * everything (probe fails at 4KB -- private window, blocked cookies, disk pressure), where the
 * payload size is a red herring. Coarse by design: it walks a fixed ladder and stops at the first
 * success, so it costs at most a handful of refused writes.
 *
 * `upTo` is the size that was actually refused, when the caller knows it. Rungs above it are
 * skipped -- they cost a multi-megabyte allocation and a write into an area already under pressure,
 * to answer a question the diagnostic never asks. The returned ceiling is therefore "the largest
 * rung accepted, having tried nothing bigger than the payload needed", which is exactly the
 * comparison the report makes (`ceiling` against `chars`).
 *
 * NOTHING IN HERE IS REPORTED. Every failure raised by this function IS the measurement, so the
 * sink is held off for the duration -- a probe that reported itself as an incident would notify the
 * consumer that is currently running -- and the area is resolved with a bare `try` rather than
 * `resolveArea`, which would warn. `runWithoutFailureSink` silences the sink but NOT the
 * once-per-page console warning, so an area getter that refuses would otherwise burn the single
 * `write` warning on a probe artifact and hide the next genuine refusal for the rest of the page.
 */
export function probeWriteCeiling(area: StorageArea, upTo?: number | null): number | null {
  return runWithoutFailureSink(() => {
    let storage: Storage;
    try {
      storage = area === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      // The getter itself refusing is the answer, not an incident: nothing is storable at any size.
      return null;
    }
    // Anything a previous probe failed to clean up is dead weight in the area about to be measured.
    purgeCeilingProbeKey(area);
    for (const size of ceilingProbeSizes(upTo)) {
      try {
        storage.setItem(CEILING_PROBE_KEY, 'x'.repeat(size));
        return size;
      } catch {
        // Too big for what is left; try the next rung down.
      } finally {
        // A REFUSED write stored nothing, but an ACCEPTED one stored `size` characters -- and a
        // refused cleanup would strand them in the user's quota forever, since nothing else ever
        // touches this key. purgeCeilingProbeKey retries via a one-character overwrite.
        purgeCeilingProbeKey(area);
      }
    }
    return null;
  });
}
